/**
 * Personalised API documentation. The catalog is static (apiCatalog.ts) but
 * the rendering is filtered by the viewer's effective permissions, so a
 * merchant_admin sees only the endpoints they can actually call.
 *
 * Layout:
 *  - Header with viewer summary (role, scope, total visible endpoints).
 *  - Search + category filter on top of the catalog.
 *  - Sections grouped by category, each rendered as a collapsible Accordion of
 *    cards. The Server-to-Server section is rendered first because that's the
 *    most-asked question from merchants.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Code,
  Group,
  Loader,
  PasswordInput,
  ScrollArea,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconBook, IconInfoCircle, IconLock, IconLockOpen, IconSearch } from '@tabler/icons-react';
import {
  API_ENDPOINTS,
  visibleEndpoints,
  type ApiEndpoint,
  type ApiHttpMethod,
} from '../lib/apiCatalog';
import { usePermissions, useSession } from '../auth/SessionContext';
import { authApi } from '../api/auth';
import { useTranslation } from '../i18n/LocaleContext';
import { JsonView } from '../components/JsonView';

function methodColor(m: ApiHttpMethod): string {
  switch (m) {
    case 'GET':
      return 'blue';
    case 'POST':
      return 'green';
    case 'PATCH':
      return 'yellow';
    case 'PUT':
      return 'yellow';
    case 'DELETE':
      return 'red';
  }
}

/**
 * Public route component. Wraps the actual docs page behind a password
 * re-authentication gate — every visit (page navigation OR full reload)
 * forces the user to re-enter their password before the catalog is shown.
 *
 * The unlocked flag is kept in component state only; navigating away and
 * coming back triggers another prompt. We deliberately do NOT persist it to
 * sessionStorage / localStorage — the page is meant to be slightly annoying
 * to access because it leaks integration surface area to whoever has the
 * tab open.
 */
export function ApiDocsPage() {
  const { t } = useTranslation();
  const { has } = usePermissions();
  const [unlocked, setUnlocked] = useState(false);
  // Page-access gate. `has()` returns true for superusers, so they bypass.
  // Checked before the re-auth prompt so a user without the permission never
  // even sees the password step. The nav link is hidden by the same permission;
  // this guards direct-URL navigation.
  if (!has('apidocs.view')) {
    return (
      <Stack gap="md">
        <Title order={2}>{t('apiDocs.title')}</Title>
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          {t('apiDocs.accessDenied')}
        </Alert>
      </Stack>
    );
  }
  if (!unlocked) {
    return <ApiDocsReauthGate onUnlock={() => setUnlocked(true)} />;
  }
  return <ApiDocsContent />;
}

function ApiDocsReauthGate({ onUnlock }: { onUnlock: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const form = useForm({
    initialValues: { password: '' },
    validate: { password: (v) => (v.length === 0 ? t('acceptInvite.valRequired') : null) },
  });
  const mutation = useMutation({
    meta: {
      successKey: 'notify.apidoc.request.ok',
      errorKey: 'notify.apidoc.request.fail',
    },
    mutationFn: () => authApi.verifyPassword(form.values.password),
    onSuccess: () => onUnlock(),
  });

  return (
    <Center mih={420}>
      <Card withBorder w={420} shadow="sm" radius="md">
        <Stack gap="md">
          <Group gap="sm">
            <IconLock size={20} />
            <Title order={4}>{t('auth.confirmPassword')}</Title>
          </Group>
          <Text size="sm" c="dimmed">
            {t('apiDocs.reauthIntro')}
          </Text>
          <form onSubmit={form.onSubmit(() => mutation.mutate())}>
            <Stack gap="sm">
              <PasswordInput
                label={t('auth.password')}
                autoFocus
                {...form.getInputProps('password')}
              />
              <Group justify="flex-end">
                <Button
                  variant="default"
                  type="button"
                  onClick={() => navigate('/dashboard', { replace: true })}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  leftSection={<IconLockOpen size={14} />}
                  loading={mutation.isPending}
                >
                  {t('auth.unlock')}
                </Button>
              </Group>
            </Stack>
          </form>
        </Stack>
      </Card>
    </Center>
  );
}

function ApiDocsContent() {
  const { t } = useTranslation();
  const { state } = useSession();
  const perms = usePermissions();

  const ctx = useMemo(() => {
    if (state.status !== 'authenticated') return null;
    // Read userType directly off the narrowed state — the local `profile`
    // alias defined below the early-return guard is out of scope here.
    return {
      isSuperuser: perms.isSuperuser,
      userType: state.profile.userType,
      has: perms.has,
    };
  }, [state, perms]);

  const allVisible = useMemo(() => {
    if (!ctx) return [];
    return visibleEndpoints(API_ENDPOINTS, ctx);
  }, [ctx]);

  const [search, setSearch] = useState('');
  const [scopeFilter, setScopeFilter] = useState<'all' | 'merchant-api' | 'dashboard'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

  const filtered = useMemo(() => {
    return allVisible.filter((ep) => {
      if (scopeFilter !== 'all' && ep.scope !== scopeFilter) return false;
      if (categoryFilter && ep.category !== categoryFilter) return false;
      if (!search.trim()) return true;
      const q = search.trim().toLowerCase();
      return (
        ep.path.toLowerCase().includes(q) ||
        ep.title.toLowerCase().includes(q) ||
        ep.description.toLowerCase().includes(q) ||
        ep.permissions.some((p) => p.toLowerCase().includes(q))
      );
    });
  }, [allVisible, scopeFilter, categoryFilter, search]);

  // Group by category, in the order categories first appear in the catalog.
  const grouped = useMemo(() => {
    const m = new Map<string, ApiEndpoint[]>();
    for (const ep of filtered) {
      const arr = m.get(ep.category) ?? [];
      arr.push(ep);
      m.set(ep.category, arr);
    }
    return [...m.entries()];
  }, [filtered]);

  const categoryOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: { value: string; label: string; count: number }[] = [];
    for (const ep of allVisible) {
      if (seen.has(ep.category)) continue;
      seen.add(ep.category);
      out.push({
        value: ep.category,
        label: ep.category,
        count: allVisible.filter((e) => e.category === ep.category).length,
      });
    }
    return out.map((o) => ({ value: o.value, label: `${o.label} (${o.count})` }));
  }, [allVisible]);

  if (state.status === 'loading') {
    return (
      <Group justify="center" py="xl">
        <Loader />
      </Group>
    );
  }
  if (!ctx || state.status !== 'authenticated') {
    return (
      <Alert color="red" icon={<IconInfoCircle size={16} />}>
        {t('apiDocs.sessionInactive')}
      </Alert>
    );
  }
  // TS narrowing: at this point state.status === 'authenticated'.
  const profile = state.profile;

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={2}>{t('apiDocs.title')}</Title>
          <Text size="sm" c="dimmed">
            {t('apiDocs.subtitle')}
          </Text>
        </div>
      </Group>

      {/* Viewer summary */}
      <Card withBorder>
        <Group gap="lg" wrap="wrap">
          <div>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{t('apiDocs.youAre')}</Text>
            <Group gap={6}>
              <Text fw={500}>{profile.accountName}</Text>
              <Badge size="xs" color={ctx.isSuperuser ? 'red' : ctx.userType === 'internal' ? 'blue' : 'brand'} variant="light">
                {ctx.isSuperuser ? t('auth.superuser') : ctx.userType}
              </Badge>
            </Group>
            <Text size="xs" c="dimmed">{profile.email}</Text>
          </div>
          <div>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{t('apiDocs.role')}</Text>
            {perms.me?.role?.name ? (
              <Text fw={500}>{perms.me.role.name}</Text>
            ) : (
              <Text fw={500} c="dimmed">{t('apiDocs.noRole')}</Text>
            )}
            {perms.me?.role?.slug && (
              <Text size="xs" c="dimmed" ff="monospace">{perms.me.role.slug}</Text>
            )}
          </div>
          <div>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{t('apiDocs.visibleEndpoints')}</Text>
            <Text fw={500}>{t('apiDocs.visibleOf', { visible: allVisible.length, total: API_ENDPOINTS.length })}</Text>
            <Text size="xs" c="dimmed">{t('apiDocs.hiddenByPermissions', { n: API_ENDPOINTS.length - allVisible.length })}</Text>
          </div>
          <div>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{t('apiDocs.permissionsHeld')}</Text>
            <Text fw={500}>
              {ctx.isSuperuser ? t('apiDocs.allSuperuserBypass') : (perms.me?.permissions?.length ?? '—')}
            </Text>
          </div>
        </Group>
      </Card>

      {/* Filter bar */}
      <Group gap="md" wrap="wrap" align="flex-end">
        <TextInput
          label={t('apiDocs.searchLabel')}
          placeholder={t('apiDocs.searchPlaceholder')}
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          style={{ flex: 1, minWidth: 240 }}
        />
        <Select
          label={t('apiDocs.categoryLabel')}
          placeholder={t('apiDocs.categoryPlaceholder')}
          data={categoryOptions}
          value={categoryFilter}
          onChange={setCategoryFilter}
          clearable
          searchable
          style={{ width: 260 }}
        />
        <Tabs value={scopeFilter} onChange={(v) => setScopeFilter(v as 'all' | 'merchant-api' | 'dashboard')}>
          <Tabs.List>
            <Tabs.Tab value="all">{t('apiDocs.tabAll')}</Tabs.Tab>
            <Tabs.Tab value="merchant-api">{t('apiDocs.tabServerToServer')}</Tabs.Tab>
            <Tabs.Tab value="dashboard">{t('apiDocs.tabDashboard')}</Tabs.Tab>
          </Tabs.List>
        </Tabs>
      </Group>

      {grouped.length === 0 && (
        <Alert color="gray" icon={<IconBook size={16} />}>
          {t('apiDocs.noMatch')}
        </Alert>
      )}

      {grouped.map(([category, eps]) => (
        <Card withBorder padding={0} radius="md" key={category}>
          <Group
            justify="space-between"
            px="md"
            py="sm"
            style={{
              background:
                'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))',
              borderBottom:
                '1px solid light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-4))',
            }}
          >
            <Group gap="sm">
              <Title order={4}>{category}</Title>
              <Badge color="gray" variant="light" size="sm">{eps.length}</Badge>
            </Group>
          </Group>
          <Accordion variant="default" multiple chevronPosition="left">
            {eps.map((ep) => (
              <EndpointRow key={`${ep.method}-${ep.path}`} ep={ep} />
            ))}
          </Accordion>
        </Card>
      ))}
    </Stack>
  );
}

function EndpointRow({ ep }: { ep: ApiEndpoint }) {
  const { t } = useTranslation();
  return (
    <Accordion.Item value={`${ep.method}-${ep.path}`}>
      <Accordion.Control>
        <Group gap="md" wrap="nowrap" align="center">
          <Badge color={methodColor(ep.method)} variant="filled" size="md" radius="sm" style={{ minWidth: 64, textAlign: 'center' }}>
            {ep.method}
          </Badge>
          <Code style={{ fontSize: 13, background: 'transparent', padding: 0 }}>{ep.path}</Code>
          <Text size="sm" c="dimmed" style={{ flex: 1 }} truncate>{ep.title}</Text>
          {ep.scope === 'merchant-api' && (
            <Badge color="grape" variant="light" size="xs">{t('apiDocs.badgeServerToServer')}</Badge>
          )}
          {ep.userType === 'internal-only' && (
            <Badge color="blue" variant="light" size="xs" leftSection={<IconLock size={10} />}>
              {t('apiDocs.badgeInternal')}
            </Badge>
          )}
          {ep.userType === 'external-only' && (
            <Badge color="brand" variant="light" size="xs">{t('apiDocs.badgeMerchant')}</Badge>
          )}
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        <Stack gap="sm">
          <Text size="sm">{ep.description}</Text>

          {ep.baseUrl && (
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>{t('apiDocs.baseUrl')}</Text>
              <Code style={{ fontSize: 13 }}>{ep.baseUrl}</Code>
              <Text size="xs" c="dimmed" mt={2}>
                {t('apiDocs.baseUrlNote')}
              </Text>
            </div>
          )}

          {ep.permissions.length > 0 && (
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                {t('apiDocs.permissionRequired')}
              </Text>
              <Group gap={6} wrap="wrap">
                {ep.permissions.map((p) => (
                  <Badge key={p} color="gray" variant="outline" size="sm" ff="monospace">
                    {p}
                  </Badge>
                ))}
              </Group>
            </div>
          )}

          {ep.notes && (
            <Alert color="blue" icon={<IconInfoCircle size={14} />} variant="light">
              <Text size="xs">{ep.notes}</Text>
            </Alert>
          )}

          {ep.requestHeaders && ep.requestHeaders.length > 0 && (
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>{t('apiDocs.requestHeaders')}</Text>
              <Table withTableBorder withColumnBorders verticalSpacing="xs" fz="xs">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('apiDocs.colName')}</Table.Th>
                    <Table.Th style={{ width: 90 }}>{t('apiDocs.colRequired')}</Table.Th>
                    <Table.Th>{t('apiDocs.colDescription')}</Table.Th>
                    <Table.Th>{t('apiDocs.colExample')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {ep.requestHeaders.map((h) => (
                    <Table.Tr key={h.name}>
                      <Table.Td>
                        <Code style={{ fontSize: 11 }}>{h.name}</Code>
                      </Table.Td>
                      <Table.Td>
                        {h.required ? (
                          <Badge color="red" variant="light" size="xs">{t('apiDocs.required')}</Badge>
                        ) : (
                          <Badge color="gray" variant="light" size="xs">{t('apiDocs.optional')}</Badge>
                        )}
                      </Table.Td>
                      <Table.Td>{h.description}</Table.Td>
                      <Table.Td>
                        {h.example ? <Code style={{ fontSize: 11 }}>{h.example}</Code> : '—'}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>
          )}

          {ep.curlExample && (
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>{t('apiDocs.curlExample')}</Text>
              <ScrollArea>
                <Code block>{ep.curlExample}</Code>
              </ScrollArea>
            </div>
          )}

          {ep.requestBodyExample !== undefined && (
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>{t('apiDocs.requestBody')}</Text>
              <JsonView value={ep.requestBodyExample} maxHeight={300} />
            </div>
          )}

          {ep.responseExample !== undefined && (
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>{t('apiDocs.responseSample')}</Text>
              <JsonView value={ep.responseExample} maxHeight={300} />
            </div>
          )}

          {ep.paymentMethods && ep.paymentMethods.length > 0 && (
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>{t('apiDocs.paymentMethods')}</Text>
              <Table withTableBorder withColumnBorders verticalSpacing="xs" fz="xs">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: 150 }}>{t('apiDocs.colCategory')}</Table.Th>
                    <Table.Th style={{ width: 180 }}>{t('apiDocs.colCode')}</Table.Th>
                    <Table.Th>{t('apiDocs.colName')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {ep.paymentMethods.map((pm) => (
                    <Table.Tr key={pm.code}>
                      <Table.Td>{pm.category}</Table.Td>
                      <Table.Td>
                        <Code style={{ fontSize: 11 }}>{pm.code}</Code>
                      </Table.Td>
                      <Table.Td>{pm.name}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>
          )}

          {ep.responseFields && ep.responseFields.length > 0 && (
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>{t('apiDocs.responseFields')}</Text>
              <Table withTableBorder withColumnBorders verticalSpacing="xs" fz="xs">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('apiDocs.colPath')}</Table.Th>
                    <Table.Th style={{ width: 180 }}>{t('apiDocs.colType')}</Table.Th>
                    <Table.Th>{t('apiDocs.colDescription')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {ep.responseFields.map((f) => (
                    <Table.Tr key={f.path}>
                      <Table.Td>
                        <Code style={{ fontSize: 11 }}>{f.path}</Code>
                      </Table.Td>
                      <Table.Td>
                        <Code style={{ fontSize: 11 }}>{f.type}</Code>
                      </Table.Td>
                      <Table.Td>{f.description}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>
          )}

          {ep.responseHeaders && ep.responseHeaders.length > 0 && (
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>{t('apiDocs.responseHeaders')}</Text>
              <Table withTableBorder withColumnBorders verticalSpacing="xs" fz="xs">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('apiDocs.colName')}</Table.Th>
                    <Table.Th>{t('apiDocs.colDescription')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {ep.responseHeaders.map((h) => (
                    <Table.Tr key={h.name}>
                      <Table.Td>
                        <Code style={{ fontSize: 11 }}>{h.name}</Code>
                      </Table.Td>
                      <Table.Td>{h.description}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>
          )}

          {ep.errorCodes && ep.errorCodes.length > 0 && (
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>{t('apiDocs.errorCodes')}</Text>
              <Table withTableBorder withColumnBorders verticalSpacing="xs" fz="xs">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: 220 }}>{t('apiDocs.colCode')}</Table.Th>
                    <Table.Th style={{ width: 90 }}>{t('apiDocs.colHttp')}</Table.Th>
                    <Table.Th>{t('apiDocs.colDescription')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {ep.errorCodes.map((e) => (
                    <Table.Tr key={e.code}>
                      <Table.Td>
                        <Code style={{ fontSize: 11 }}>{e.code}</Code>
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          color={e.httpStatus >= 500 ? 'red' : e.httpStatus >= 400 ? 'orange' : 'gray'}
                          variant="light"
                          size="xs"
                        >
                          {e.httpStatus}
                        </Badge>
                      </Table.Td>
                      <Table.Td>{e.description}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>
          )}

          <div>
            <Text size="xs" c="dimmed">
              {t('apiDocs.fullPath')}{' '}
              <Text span ff="monospace" size="xs">
                {ep.path}
              </Text>
            </Text>
          </div>
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
}
